/* NH bag behaviour for the cart drawer and /cart (redesign 2026-09-29, creative director).
   The theme (Impulse) still owns the lines: it changes quantities through /cart/change.js and rebuilds
   [data-products] from /cart?view=ajax. This file keeps everything around the lines in sync with the live cart:
   - free-shipping progress ($100 / A$100): text, gradient fill + riding heart, heart burst when it unlocks;
   - "Pairs well with" rail: refreshed through the Section Rendering API (/cart?section_id=nh-cart-boost) when
     the products in the bag change; arrows, progress bar and "View all"; one-tap Add via /cart/add.js;
   - count pill, Afterpay line (total / 4, rounded half-up to the cent, as Afterpay does; NZ range $1-$2,000), the amount on Check out;
   - a 150ms number roll on the subtotal, the Check out amount and the line whose quantity changed;
   - Remove in place (the × and the bin that replaces "−" at quantity 1) via /cart/change.js, then an
     "Undo" toast that puts the line back with /cart/add.js. If the request fails the link is followed.
   Events out: "cart:updated" (detail.cart) for the dock badge, "cart:build" to rebuild lines.
   Markup: snippets/cart-drawer, cart-item, nh-cart-boost-bar, nh-cart-boost-rail, nh-i-cart-trust, sections/main-cart. */
(() => {
  if (window.nhCartBoost) return;

  const THRESHOLD = 10000;
  const AP_MIN = 100;
  const AP_MAX = 200000;
  const SECTION_ID = 'nh-cart-boost';
  const doc = document;
  const isCartPage = doc.body.classList.contains('template-cart');
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const state = { seq: 0, railKey: '', railCtrl: null, timer: 0, loaded: false, lastTotal: null, undo: null, toastTimer: 0, rollKey: null };

  /** On /cart the drawer markup is present but inactive; only touch the live copy. */
  const live = (sel) => [...doc.querySelectorAll(sel)].filter((el) => !(isCartPage && el.closest('#CartDrawer')));
  const activeForm = () => doc.getElementById(isCartPage ? 'CartPageForm' : 'CartDrawerForm');

  /** @param {number} cents */
  const money = (cents) => {
    try {
      if (window.theme && theme.Currency && theme.settings && theme.settings.moneyFormat) return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
    } catch (e) { /* fall through */ }
    return `$${(cents / 100).toFixed(2)}`;
  };
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const status = () => doc.querySelector('#CartDrawer [data-nhcd-status]');
  const say = (msg) => {
    const el = status();
    if (!el) return;
    el.textContent = '';
    requestAnimationFrame(() => { el.textContent = msg; });
  };
  const roll = (el) => {
    if (!el || reduce.matches) return;
    el.classList.remove('nhb-roll');
    void el.offsetWidth;
    el.classList.add('nhb-roll');
  };
  /** @param {Element} el @param {string} text */
  const setText = (el, text) => {
    if (!el || el.textContent.trim() === text) return;
    el.textContent = text;
    roll(el);
  };
  const jsonFetch = (url, body) => fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify(body),
  });

  /* ---------- free-shipping progress ---------- */
  /** @param {{ total_price: number, item_count: number }} cart */
  function renderBar(cart) {
    for (const bar of live('[data-nhcb-bar]')) {
      const threshold = Number(bar.dataset.threshold) || THRESHOLD;
      const remaining = Math.max(0, threshold - cart.total_price);
      const done = remaining === 0;
      const wasDone = bar.classList.contains('nhb-ship--done');
      const prevP = parseFloat(bar.style.getPropertyValue('--nhcb-p')) || 0;
      const p = Math.min(1, cart.total_price / threshold);
      bar.hidden = cart.item_count === 0 && !bar.closest('#CartDrawer');
      bar.style.setProperty('--nhcb-p', p.toFixed(4));
      bar.classList.toggle('nhb-ship--done', done);
      if (Math.abs(p - prevP) > 0.001 && state.loaded) {
        bar.classList.remove('nhb-ship--beat');
        void bar.offsetWidth;
        bar.classList.add('nhb-ship--beat');
      }
      if (done && !wasDone && state.lastTotal !== null) {
        bar.classList.remove('nhb-ship--pop');
        void bar.offsetWidth;
        bar.classList.add('nhb-ship--pop');
      }
      const text = bar.querySelector('[data-nhcb-text]');
      const key = done ? 'done' : String(remaining);
      if (!text || text.dataset.key === key) continue;
      text.dataset.key = key;
      text.innerHTML = done ? 'Free shipping <strong>unlocked</strong>' : `You’re <strong>${money(remaining)}</strong> away from free shipping`;
    }
  }

  /* ---------- totals, count, Afterpay ---------- */
  function renderTotals(cart) {
    for (const el of live('[data-nhb-total], [data-subtotal]')) setText(el, money(cart.total_price)); // subtotal + Check out move together (the theme re-renders the same value later)
    for (const ap of live('[data-nhb-ap]')) {
      ap.hidden = cart.total_price < AP_MIN || cart.total_price > AP_MAX;
      setText(ap.querySelector('[data-nhb-ap-amt]'), money(Math.round(cart.total_price / 4)));
    }
    for (const c of live('[data-nhb-count]')) {
      c.hidden = cart.item_count === 0;
      const n = c.querySelector('[data-nhb-count-n]');
      const sr = c.querySelector('[data-nhb-count-sr]');
      if (n && n.textContent !== String(cart.item_count)) { n.textContent = String(cart.item_count); roll(n); }
      if (sr) sr.textContent = `, ${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}`;
    }
  }

  /* ---------- rail ---------- */
  /* assets/nh-rails.js gives [data-nh-rail="input"] rails wheel (vertical wheel -> sideways, then page), drag
     and snap; rails rendered after load must be announced. */
  const scanRails = () => doc.dispatchEvent(new Event('nh:rails:scan'));

  function initRail(scope) {
    for (const rail of scope.querySelectorAll('.nhb-rail:not(.nhb-rail--skeleton)')) {
      if (rail.dataset.nhbInit) continue;
      rail.dataset.nhbInit = '1';
      const list = rail.querySelector('[data-nhb-rail-list]');
      const prev = rail.querySelector('[data-nhb-rail-prev]');
      const next = rail.querySelector('[data-nhb-rail-next]');
      const bar = rail.querySelector('[data-nhb-rail-bar]');
      if (!list) continue;
      const update = () => {
        const max = list.scrollWidth - list.clientWidth;
        const scrollable = max > 2 && !rail.classList.contains('nhb-rail--stack');
        rail.classList.toggle('nhb-rail--static', !scrollable);
        if (!scrollable) return;
        const frac = Math.min(1, Math.max(0, list.scrollLeft / max));
        const w = Math.max(12, (list.clientWidth / list.scrollWidth) * 100);
        if (bar) {
          bar.style.setProperty('--nhb-rw', `${w}%`);
          bar.style.setProperty('--nhb-rx', `${frac * ((100 - w) / w) * 100}%`);
        }
        if (prev) prev.disabled = frac < 0.02;
        if (next) next.disabled = frac > 0.98;
      };
      const step = (dir) => {
        const card = list.querySelector('.nhb-pick');
        const by = card ? card.getBoundingClientRect().width + 10 : list.clientWidth * 0.8;
        list.scrollBy({ left: dir * by, behavior: reduce.matches ? 'auto' : 'smooth' });
      };
      prev?.addEventListener('click', () => step(-1));
      next?.addEventListener('click', () => step(1));
      list.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(update).observe(list);
      update();
    }
    scanRails();
  }

  /* Drawer only: when the scroll area has room, show every pick as a vertical stack (no dead space under a short
     bag); when it does not, fall back to the horizontal rail with its peek, arrows, progress and View all. */
  function fitRail() {
    const scroll = doc.querySelector('#CartDrawer [data-nhb-scroll]');
    const rail = scroll && scroll.querySelector('.nhb-rail:not(.nhb-rail--skeleton)');
    if (!rail || !scroll.clientHeight) return;
    const cards = [...rail.querySelectorAll('.nhb-pick')];
    const fits = () => scroll.scrollHeight <= scroll.clientHeight + 1;
    for (const c of cards) c.hidden = false;
    rail.classList.add('nhb-rail--stack');
    let ok = fits();
    for (let n = cards.length - 1; !ok && n >= 2; n--) { cards[n].hidden = true; ok = fits(); }
    if (!ok) { for (const c of cards) c.hidden = false; rail.classList.remove('nhb-rail--stack'); }
    rail.querySelector('[data-nhb-rail-list]')?.dispatchEvent(new Event('scroll'));
  }

  async function renderRail(cart) {
    const slots = live('[data-nhcb-rail]');
    if (!slots.length) return;
    if (cart.item_count === 0) {
      state.railCtrl?.abort();
      state.railKey = '';
      for (const slot of slots) { slot.hidden = true; slot.replaceChildren(); }
      return;
    }
    const key = cart.items.map((item) => item.product_id).sort().join(',');
    const ready = slots.every((slot) => slot.querySelector('[data-nhcb-rail-inner]'));
    if (!state.railKey && ready) state.railKey = key; // server-rendered list (/cart) is already current
    if (key === state.railKey && ready) {
      for (const slot of slots) { slot.hidden = false; initRail(slot); }
      requestAnimationFrame(fitRail);
      return;
    }
    state.railCtrl?.abort();
    const ctrl = new AbortController();
    state.railCtrl = ctrl;
    try {
      const url = new URL(`${root}cart`, location.origin);
      url.searchParams.set('section_id', SECTION_ID);
      url.searchParams.set('t', String(Date.now()));
      const res = await fetch(url, { signal: ctrl.signal, credentials: 'same-origin', cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const parsed = new DOMParser().parseFromString(await res.text(), 'text/html');
      const fresh = parsed.querySelector('[data-nhcb-rail-inner]');
      state.railKey = key;
      for (const slot of slots) {
        const hadFocus = slot.contains(doc.activeElement);
        const h = slot.getBoundingClientRect().height;
        if (h) slot.style.minHeight = `${Math.round(h)}px`;
        if (fresh) slot.replaceChildren(fresh.cloneNode(true));
        else slot.replaceChildren();
        slot.hidden = !fresh;
        initRail(slot);
        requestAnimationFrame(() => { slot.style.minHeight = ''; });
        if (hadFocus) slot.querySelector('[data-nhcb-add]')?.focus({ preventScroll: true });
      }
      requestAnimationFrame(fitRail);
    } catch (error) {
      if (error.name === 'AbortError') return;
      for (const slot of slots) if (!slot.querySelector('[data-nhcb-rail-inner]')) { slot.replaceChildren(); slot.hidden = true; }
    }
  }

  /* ---------- render + refresh ---------- */
  function render(cart) {
    if (!cart || typeof cart.total_price !== 'number' || !Array.isArray(cart.items)) { scheduleRefresh(); return; }
    if (isCartPage && cart.item_count === 0 && doc.getElementById('CartPageForm')) { location.reload(); return; }
    renderBar(cart);
    renderTotals(cart);
    renderRail(cart);
    state.loaded = true;
    state.lastTotal = cart.total_price;
  }
  async function refresh() {
    const seq = ++state.seq;
    try {
      const res = await fetch(`${root}cart.js?t=${Date.now()}`, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      if (seq === state.seq) render(cart);
    } catch (e) { /* the next cart change re-syncs */ }
  }
  const scheduleRefresh = () => { clearTimeout(state.timer); state.timer = setTimeout(refresh, 120); };

  /* ---------- rail Add ---------- */
  async function addPick(button) {
    if (button.getAttribute('aria-busy') === 'true') return;
    const rail = button.closest('[data-nhcb-rail-inner]');
    const note = rail && rail.querySelector('[data-nhcb-status]');
    const label = button.querySelector('[data-nhcb-label]');
    const title = button.closest('.nhb-pick')?.querySelector('.nhb-pick__title')?.textContent.trim() || 'It';
    if (note) { note.textContent = ''; note.classList.remove('is-error'); }
    button.setAttribute('aria-busy', 'true');
    if (label) label.textContent = 'Adding';
    try {
      const res = await jsonFetch(`${root}cart/add.js`, { items: [{ id: Number(button.dataset.variantId), quantity: 1 }] });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.description || data.message || '');
      button.removeAttribute('aria-busy');
      button.classList.add('is-added');
      if (label) label.textContent = 'Added';
      if (note) note.textContent = `${title} is in your bag.`;
      say(`${title} added to your bag`);
      doc.dispatchEvent(new CustomEvent('cart:build'));
      await refresh();
      setTimeout(() => { state.railKey = ''; refresh(); }, 900);
    } catch (error) {
      button.removeAttribute('aria-busy');
      if (label) label.textContent = 'Add';
      if (note) {
        note.classList.add('is-error');
        note.textContent = `We couldn’t add that. ${error && error.message ? `${error.message} ` : ''}Please try again.`.replace(/\s+/g, ' ');
      }
    }
  }

  /* ---------- remove in place + undo ---------- */
  const toastFor = (form) => {
    if (!form) return null;
    let toast = form.querySelector('[data-nhb-toast]');
    if (!toast && form.id === 'CartPageForm') {
      toast = doc.createElement('div');
      toast.className = 'nhb-toast';
      toast.dataset.nhbToast = '';
      toast.hidden = true;
      toast.innerHTML = '<p class="nhb-toast__msg" data-nhb-toast-msg></p><button type="button" class="nhb-toast__undo" data-nhb-undo>Undo</button>';
      form.appendChild(toast);
    }
    return toast;
  };
  const hideToast = () => {
    clearTimeout(state.toastTimer);
    for (const t of doc.querySelectorAll('[data-nhb-toast]')) {
      if (t.contains(doc.activeElement)) (activeForm()?.querySelector('[data-nhb-remove]') || doc.getElementById('CartDrawerTitle'))?.focus({ preventScroll: true });
      t.hidden = true;
    }
  };
  const showToast = (form, name) => {
    const toast = toastFor(form);
    if (!toast) return;
    const foot = form.querySelector('.nhb-foot');
    if (foot) form.querySelector('.nhb-body')?.style.setProperty('--nhb-foot-h', `${foot.offsetHeight}px`);
    toast.querySelector('[data-nhb-toast-msg]').textContent = `Removed ${name}.`;
    toast.hidden = false;
    clearTimeout(state.toastTimer);
    state.toastTimer = setTimeout(hideToast, 6000);
  };

  async function removeLine(trigger) {
    const line = trigger.closest('[data-nhb-line]');
    const form = trigger.closest('form');
    if (!line || !form || line.classList.contains('is-removing')) return;
    const key = line.dataset.key;
    const name = line.dataset.title || 'Item';
    const lines = [...form.querySelectorAll('[data-nhb-line]')];
    const i = lines.indexOf(line);
    const neighbour = lines[i + 1] || lines[i - 1] || null;
    line.classList.add('is-removing');
    line.setAttribute('aria-busy', 'true');
    say(`Removing ${name}`);
    try {
      const res = await jsonFetch(`${root}cart/change.js`, { id: key, quantity: 0 });
      if (!res.ok) throw new Error(String(res.status));
      const cart = await res.json();
      state.undo = { id: Number(line.dataset.variantId), quantity: Number(line.dataset.qty) || 1, name };
      state.refocus = neighbour ? { key: neighbour.dataset.key, sel: '[data-nhb-remove]' } : null;
      doc.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      doc.dispatchEvent(new CustomEvent('cart:build'));
      if (cart.item_count && !(isCartPage && form.id === 'CartPageForm' && !cart.item_count)) showToast(form, name);
      say(`Removed ${name}. ${cart.item_count ? `Your bag has ${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}. Undo is available.` : 'Your bag is empty'}`);
      if (!cart.item_count) setTimeout(() => doc.getElementById('CartDrawerTitle')?.focus({ preventScroll: true }), 400);
    } catch (err) {
      const href = line.querySelector('[data-nhb-remove]')?.getAttribute('href');
      if (href) location.href = href;
    }
  }

  async function undoRemove() {
    const u = state.undo;
    // the lines are about to be rebuilt: park focus on the stable heading first
    const head = doc.getElementById(isCartPage ? 'NhbpTitle' : 'CartDrawerTitle');
    if (head) head.focus({ preventScroll: true });
    hideToast();
    if (!u) return;
    state.undo = null;
    try {
      const res = await jsonFetch(`${root}cart/add.js`, { items: [{ id: u.id, quantity: u.quantity }] });
      if (!res.ok) throw new Error(String(res.status));
      doc.dispatchEvent(new CustomEvent('cart:build'));
      say(`${u.name} is back in your bag`);
      refresh();
    } catch (e) {
      say(`We couldn’t put ${u.name} back. Please add it again.`);
    }
  }

  /* capture phase: runs before the theme's own "−" handler, so "−" at quantity 1 removes the line */
  doc.addEventListener('click', (event) => {
    const t = event.target instanceof Element ? event.target : null;
    if (!t) return;
    const remove = t.closest('[data-nhb-remove], [data-nhb-remove-one]');
    if (remove && remove.closest('#CartDrawerForm, #CartPageForm')) {
      if (event.metaKey || event.ctrlKey || event.shiftKey) return;
      event.preventDefault();
      event.stopPropagation();
      removeLine(remove);
      return;
    }
    const adj = t.closest('.js-qty__adjust');
    if (adj) {
      const line = adj.closest('[data-nhb-line]');
      if (line) state.rollKey = line.dataset.key;
      return;
    }
    if (t.closest('[data-nhb-undo]')) { event.preventDefault(); undoRemove(); return; }
    const add = t.closest('[data-nhcb-add]');
    if (add) { event.preventDefault(); addPick(add); }
  }, true);

  /* after every rebuild of the lines: roll the changed line, put focus back where it was */
  for (const form of [doc.getElementById('CartDrawerForm'), doc.getElementById('CartPageForm')]) {
    const products = form?.querySelector('[data-products]');
    if (!products || !('MutationObserver' in window)) continue;
    new MutationObserver(() => {
      requestAnimationFrame(fitRail);
      if (state.rollKey) {
        const line = [...products.querySelectorAll('[data-nhb-line]')].find((n) => n.dataset.key === state.rollKey);
        state.rollKey = null;
        if (line) { roll(line.querySelector('.nhb-line__now')); }
      }
      if (state.refocus && form.contains(doc.activeElement) === false) {
        const { key, sel } = state.refocus;
        state.refocus = null;
        const line = [...products.querySelectorAll('[data-nhb-line]')].find((n) => n.dataset.key === key);
        (line?.querySelector(sel))?.focus({ preventScroll: true });
      } else {
        state.refocus = null;
      }
    }).observe(products, { childList: true });
  }

  /* ---------- listeners ---------- */
  doc.addEventListener('cart:updated', (event) => {
    const cart = event.detail && event.detail.cart;
    if (cart && typeof cart.total_price === 'number' && Array.isArray(cart.items)) {
      clearTimeout(state.timer);
      state.seq++;
      render(cart);
    } else scheduleRefresh();
  });
  doc.addEventListener('ajaxProduct:added', scheduleRefresh);
  const form = activeForm();
  if (form) {
    new MutationObserver((records) => {
      for (const r of records) {
        const node = r.target.nodeType === 1 ? r.target : r.target.parentElement;
        if (node && node.closest('[data-subtotal]')) {
          const sub = node.closest('[data-subtotal]');
          roll(sub);
          scheduleRefresh();
          return;
        }
      }
    }).observe(form, { childList: true, characterData: true, subtree: true });
  }
  const drawer = doc.getElementById('CartDrawer');
  if (drawer && !isCartPage) {
    new MutationObserver(() => {
      if (drawer.classList.contains('drawer--is-open') && !state.loaded) refresh();
      if (drawer.classList.contains('drawer--is-open')) setTimeout(fitRail, 60);
      if (!drawer.classList.contains('drawer--is-open')) hideToast();
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }
  window.addEventListener('resize', () => requestAnimationFrame(fitRail), { passive: true });
  if (isCartPage) {
    initRail(doc);
    refresh();
  }

  window.nhCartBoost = { refresh };
})();
